/**
 * TransparencySettingsModal.tsx
 *
 * Standard React transparency settings modal using HTML/CSS
 * Controls text area background opacity
 */
import React from 'react';
import { useSettings } from '../../services/SettingsContext';
import { useTheme } from '../../services/ThemeContext';
import { ScrollableModal } from './Modal';

interface TransparencySettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const TransparencySettingsModal: React.FC<TransparencySettingsModalProps> = ({ isOpen, onClose }) => {
  const { transparencySettings, setTransparencySettings } = useSettings();
  const { currentTheme: colors } = useTheme();

  if (!isOpen) return null;

  return (
    <ScrollableModal isOpen={isOpen} onClose={onClose} title="Text Area Transparency">
      {/* Opacity Slider */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ fontSize: 12, color: colors.textMuted, marginBottom: 8 }}>
          Text Area Opacity (0-100%)
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ color: colors.textMuted, fontSize: 14 }}>0%</span>
          <input
            data-testid="transparency-slider"
            type="range"
            min={0}
            max={100}
            step={5}
            value={transparencySettings.textAreaOpacity}
            onChange={e => setTransparencySettings({ textAreaOpacity: parseInt(e.target.value) })}
            style={{ flex: 1, accentColor: colors.primary }}
          />
          <span style={{ color: colors.textMuted, fontSize: 14 }}>100%</span>
          <span style={{ color: colors.primary, fontWeight: 'bold', fontSize: 14, minWidth: 45, textAlign: 'right' }}>
            {transparencySettings.textAreaOpacity}%
          </span>
        </div>
      </div>

      {/* Preview Boxes */}
      <div style={{ fontSize: 12, color: colors.textMuted, marginBottom: 8 }}>
        Preview
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {/* Dark background preview */}
        <div
          style={{
            padding: 16,
            borderWidth: 1,
            borderColor: colors.border,
            borderStyle: 'solid',
            borderRadius: 8,
            backgroundColor: `rgba(0, 0, 0, ${transparencySettings.textAreaOpacity / 100})`,
          }}
        >
          <div style={{ fontSize: 14, color: colors.textMain }}>
            This is how text areas appear on dark backgrounds with {transparencySettings.textAreaOpacity}% opacity
          </div>
        </div>

        {/* Light background preview */}
        <div
          style={{
            padding: 16,
            borderWidth: 1,
            borderColor: colors.border,
            borderStyle: 'solid',
            borderRadius: 8,
            backgroundColor: `rgba(255, 255, 255, ${transparencySettings.textAreaOpacity / 100})`,
          }}
        >
          <div style={{ fontSize: 14, color: colors.textMain }}>
            This is how text areas appear on light backgrounds with {transparencySettings.textAreaOpacity}% opacity
          </div>
        </div>
      </div>

      {/* Description */}
      <div style={{ marginTop: 24, padding: 12, backgroundColor: `${colors.primary}11`, borderRadius: 8, borderStyle: 'solid', borderWidth: 1, borderColor: colors.primary }}>
        <div style={{ fontSize: 12, color: colors.textMain }}>
          <strong style={{ color: colors.primary }}>Note:</strong> Higher values make text areas more opaque (solid),
          while lower values make them more transparent (see-through). The default is 75%.
        </div>
      </div>
    </ScrollableModal>
  );
};

export { TransparencySettingsModal };
